class CusaGuardiansTiers extends HTMLElement {
	constructor() {
		super();

		// Cache data we'll need
		this.templateName = this.dataset.templateName?.toLowerCase();
		this.currentGuardiansTier = this.dataset.currentTier || null;
		this.currentGuardiansSubscriptionType = this.dataset.currentSubscriptionType || null;
		this.hasStripeMembership = this.dataset.hasStripeMembership || null;

		// Grab all the DOM elements we'll use
		this.registerForm = document.querySelector('[data-register-form]');
		this.primarySubmitBtn = this.registerForm?.querySelector('[data-submit-button]');
		this.cardBtns = this.querySelectorAll('.cusa-guardians-tier-btn');
		this.submitBtns = document.querySelectorAll('[data-submit-button]');
		this.externalSubmitBtn = this.querySelector('#cusa-guardian-tiers-external-submit-btn');

		// Figure out which tier is currently selected
		this.selectedTier = this.querySelector("[data-selected='true']");
		this.selectedTierBtn = this.selectedTier?.querySelector('.cusa-guardians-tier-btn');
		this.selectedTierName = this.selectedTier?.querySelector('.tier-name')?.textContent;

		// Cancellation modal stuff
		this.cancellationForm = this.querySelector('#cancellation-warning');
		this.cancellationModalBtn = this.querySelector('[data-cancellation-action]');
		this.cancellationConfirmationBtn = document.querySelector('#confirmCancellationBtn');

		this.passedTierParam = checkForUrlParam('guardians-tier');
	}
	connectedCallback() {
		// Wire up all the event listeners
		this.cardBtns.forEach((btn) => {
			btn.addEventListener('click', this.handleTierBtnClick, false);
			if (this.cancellationModalBtn && btn === this.cancellationModalBtn) {
				btn.addEventListener('click', this.triggerCancellationPopup, false);
			}
		});

		this.cancellationConfirmationBtn?.addEventListener('click', this.processCancellation, false);
		this.registerForm?.addEventListener('submit', this.disableSubmitBtns, false);

		if (this.templateName !== 'register') {
			this.submitBtns.forEach((btn) => {
				btn.addEventListener('click', this.handleNonFormSubmit, false);
			});
		}

		this.handleTierVisibility();
	}

	disconnectedCallback() {
		// Clean up when component gets removed
		this.cardBtns.forEach((btn) => {
			btn.removeEventListener('click', this.handleTierBtnClick, false);
		});

		this.registerForm?.removeEventListener('submit', this.disableSubmitBtns, false);

		if (this.templateName !== 'register') {
			this.submitBtns.forEach((btn) => {
				btn.removeEventListener('click', this.handleNonFormSubmit, false);
			});
		}
	}

	/**
	 * Updates the 'return_to' child element in the registration form
	 * @param {HTMLElement} selectedBtn References the currently selected
	 * button element
	 */
	updateFormDestination = (selectedBtn) => {
		const url = selectedBtn.dataset.monthlyUrl;
		if (url && this.registerForm) {
			this.registerForm.querySelector("input[name='return_to']").value = url;
		}
	};

	/**
	 * Updates the appearance of tier card buttons based on the currently selected tier and billing
	 * @param {HTMLElement} btn The button element to update
	 */
	updateButtonState = (btn) => {
		const card = btn.closest('.cusa-guardians-card');
		const isSelected = btn === this.selectedTierBtn;

		if (isSelected) {
			btn.textContent = 'Selected';
			btn.classList.remove('button--primary');
			btn.classList.add('button--secondary');

			// If there's no registration form on the page
			// AND the customer's current membership tier isn't null
			// AND it matches the current card's tier
			// AND the selected billing is the same as the customer's current billing
			// disable the card btn
			const shouldDisable =
				!this.registerForm &&
				this.currentGuardiansTier &&
				this.selectedTierName === this.currentGuardiansTier &&
				this.dataset.selectedBilling === this.currentGuardiansSubscriptionType;

			btn.toggleAttribute('disabled', shouldDisable);
		} else {
			card.setAttribute('data-selected', false);
			card.classList.remove('js-tier-selecting');
			btn.textContent = 'Select';
			btn.classList.remove('button--secondary');
			btn.classList.add('button--primary');
			btn.removeAttribute('disabled');
		}
	};

	/**
	 * Runs through all tier card buttons and updates each using the 'updateButtonState' function
	 */
	updateButtonStates = () => {
		this.cardBtns.forEach((btn) => this.updateButtonState(btn));

		// Now that all of the tier card buttons have been updated, double check
		// the Update Subscription button and update it accordingly
		if (this.currentGuardiansTier && !this.registerForm && this.externalSubmitBtn) {
			const shouldDisable =
				this.selectedTierBtn?.dataset.cancellationAction ||
				(this.selectedTierName === this.currentGuardiansTier &&
					this.dataset.selectedBilling === this.currentGuardiansSubscriptionType);

			this.externalSubmitBtn.toggleAttribute('disabled', shouldDisable);
			this.externalSubmitBtn.classList.toggle('disabled', shouldDisable);
		}
	};

	/**
	 * Scrolls to the update button and gives it a little pulse animation
	 */
	scrollToUpdateButton = () => {
		if (!this.externalSubmitBtn) return;

		setTimeout(() => {
			this.externalSubmitBtn.scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			});

			// Little pulse to draw attention
			this.externalSubmitBtn.classList.add('pulse-highlight');
			setTimeout(() => {
				this.externalSubmitBtn.classList.remove('pulse-highlight');
			}, 2000);
		}, 100);
	};

	/**
	 * Handles clicks specifically on the buttons within Guardian tier cards. This
	 * calls other functions to manage the registration form destination if
	 * applicable. It also updates the appearance of all tier buttons.
	 * @param {event} e Click event on a Guardians tier button
	 */
	handleTierBtnClick = (e) => {
		this.registerForm && this.updateFormDestination(e.target);

		this.selectedTierBtn = e.target;
		this.selectedTier = this.selectedTierBtn.closest('[data-selected]');
		this.selectedTier.setAttribute('data-selected', true);

		const tierName = this.selectedTier.querySelector('.tier-name').textContent;

		// Add a visual indicator if they're selecting a different tier
		if (tierName !== this.currentGuardiansTier) {
			this.selectedTier.classList.add('js-tier-selecting');
		}

		this.selectedTierName = tierName;
		this.updateButtonStates();
		this.resetButtonState();

		// Auto-scroll to the update button if they picked a different tier,
		// but skip this when the action is cancellation (Ally downgrade),
		// to keep focus on the confirmation modal UX.
		if (
			!this.registerForm &&
			tierName !== this.currentGuardiansTier &&
			!this.selectedTierBtn?.dataset?.cancellationAction
		) {
			this.scrollToUpdateButton();
		}
	};

	/**
	 * Shows/hides loading spinner on buttons
	 */
	toggleSpinnerState = (btn, enabled) => {
		if (!btn) return;

		const span = btn.querySelector('span');
		const spinner = btn.querySelector('.loading__spinner');

		if (enabled) {
			btn.disabled = true;
			span.style.opacity = 0;
			btn.classList.add('button--secondary');
			spinner?.classList.remove('hidden');
		} else {
			btn.disabled = false;
			span.style.opacity = 1;
			btn.classList.remove('button--secondary');
			spinner?.classList.add('hidden');
		}
	};

	/**
	 * Loops through all [data-submit-button] buttons on the page and *disables* them
	 * using the 'addSpinnerState' function.
	 */
	disableSubmitBtns = () => {
		this.submitBtns.forEach((btn) => this.toggleSpinnerState(btn, true));
	};

	/**
	 * Loops through all [data-submit-button] buttons on the page and *enables* them
	 * using the 'removeSpinnerState' function.
	 */
	enableSubmitBtns = () => {
		this.submitBtns.forEach((btn) => this.toggleSpinnerState(btn, false));
	};

	/**
	 * Retrieves the URL corresponding to the selected tier's monthly plan.
	 * Used as the redirect target for checkout or cart operations.
	 * @returns {string} The monthly URL from the selected tier's button data attribute.
	 */
	getSelectedTierUrl = () => {
		return this.selectedTierBtn.dataset.monthlyUrl;
	};

	/**
	 * Handles the 'submission' of a Guardians tier change when the registration form
	 * isn't present. This will redirect to a prepopulated cart url in the event of
	 * the current tier being 'Ally' or will contact the Guardians middleware for
	 * changes to a paid tier (up or down).
	 * @returns null in the event of a redirect.
	 */
	handleNonFormSubmit = async (e) => {
		console.log('>>>>>>>>>>>>>>');
		console.log(e);
		console.log('>>>>>>>>>>>>>>');
		this.disableSubmitBtns();

		// If, and only if, the current tier is Ally AND there's no registration
		// form on the page — meaning it's the membership page — then we want to
		// send people into the checkout funnel using the selected 'upgrade' url.
		// If this isn't the case though, ie: it's the membership page and the
		// current tier is anything other than Ally,
		// then connect with the middleware instead.
		if (!this.registerForm) {
			if (this.currentGuardiansTier.toLocaleLowerCase() === 'ally' || this.hasStripeMembership === 'true') {
				if (e.target.id !== 'confirmCancellationBtn') {
					console.log(this.getSelectedTierUrl());
					window.location.href = this.getSelectedTierUrl();
					return;
				}
			}
		}

		const tierName = this.querySelector("[data-selected='true']")
			.querySelector('.tier-name')
			.textContent.toLocaleLowerCase();
		const billingFrequency = tierName === 'ally' ? 'monthly' : this.dataset.selectedBilling.toLocaleLowerCase();

		const bodyData = {
			new_tier_handle: tierName,
			billing_frequency: billingFrequency
		};

		const reqStatusToast = CusaToastNotification.create({
			message: 'Please remain on the page while we process your request…',
			type: 'info',
			duration: 'infinite'
		});

		console.log(bodyData);

		try {
			const result = await cusaFetch(CUSA_ENDPOINTS.memberships, 'POST', JSON.stringify(bodyData));
			console.log(result);
			CusaToastNotification.dismiss(reqStatusToast);

			// Disable the update button until they select a different tier
			this.disableUpdateButton();

			// Show success message
			CusaToastNotification.create({
				message: 'Your membership has been successfully updated!',
				type: 'success',
				duration: '2000',
				title: 'Success!'
			});
		} catch (err) {
			CusaToastNotification.dismiss(reqStatusToast);
			console.error('Membership update failed:', err.message);

			if (err.status && (err.status < 200 || err.status >= 300)) {
				const userMessage = `There was an error processing your request. Please try again in 5 minutes. If you receive the error again, <a href='https://help.greatergood.com/hc/en-us/articles/360022735753-Contact-Information?_gl=1*1sdlmtj*_gcl_au*MjU0ODQ2NDgyLjE3NDI5MzMzODc.*_ga*NTc4MTU3MDk2LjE3MTE2NDMwMjg.*_ga_91Y25D7P28*czE3NDc5NDg1ODEkbzU3JGcxJHQxNzQ3OTQ4NTg1JGowJGwwJGgw'>please contact Customer Service</a>.`;
				CusaToastNotification.create({
					message: userMessage,
					type: 'error',
					duration: 'infinite',
					title: 'Error'
				});
			}
		}
		this.enableSubmitBtns();
		this.resetCancellationPopup();
	};

	/**
	 * Processes the confirmed cancellation of a paid Guardians tier down to the
	 * 'Ally' from within the cancellation modal's form.
	 * @param {event} e Click event within the cancellation form
	 */
	processCancellation = (e) => {
		e.preventDefault();
		this.toggleSpinnerState(e.target, true);
		this.handleNonFormSubmit(e);
	};

	/**
	 * Triggers a modal for confirming the cancellation of a paid Guardians tier
	 * down to 'Ally'.
	 */
	triggerCancellationPopup = () => {
		this.cancellationForm.showModal();
	};

	/**
	 * Resets the cancellation popup to its default visual state and closes the modal.
	 */
	resetCancellationPopup = () => {
		this.toggleSpinnerState(this.cancellationConfirmationBtn, false);
		this.cancellationConfirmationBtn.classList.add('button--secondary');
		this.cancellationForm.close();
	};

	/**
	 * Handles the visibility and configuration of the form based on the passed tier URL parameter.
	 *
	 * If a tier is specified via `this.passedTierParam`, it:
	 * - Logs the tier.
	 * - Updates the form's return URL based on the selected tier's monthly URL.
	 * - Removes embedded styles related to the tier section.
	 * - Resets the primary submit button's classes.
	 *
	 * If no tier is specified, it:
	 * - Calls `setFormDestToSelectedTier()` to default to the currently selected tier.
	 * - Ensures the tier selection section is visible.
	 */
	handleTierVisibility = () => {
		if (this.passedTierParam) {
			console.log(this.passedTierParam);
			const dynamicRegistrationText = document.querySelector('#dynamic-registration-text');

			const monthlyUrl = document
				.querySelector(`[data-guardians-tier="${this.passedTierParam}"]`)
				.querySelector('[data-monthly-url]').dataset.monthlyUrl;

			this.registerForm.querySelector('[name="return_to"]').value = monthlyUrl;

			document.querySelector('cusa-guardians-tiers').closest('.shopify-section').querySelector('style').remove();

			this.primarySubmitBtn.removeAttribute('class');

			// Show dynamic registration text with tier name
			if (dynamicRegistrationText) {
				const tierNameSpan = dynamicRegistrationText.querySelector('.tier-name');
				if (tierNameSpan) {
					tierNameSpan.textContent = this.passedTierParam;
				}
				dynamicRegistrationText.classList.remove('hidden');
			}
		} else {
			this.setFormDestToSelectedTier();
			document.querySelector('cusa-guardians-tiers').closest('section').classList.remove('hidden');
		}
	};

	/**
	 * Sets the form's return destination URL to match the selected tier's monthly URL.
	 *
	 * This function finds the `.cusa-guardians-card` element with `data-selected="true"`,
	 * then updates the `return_to` input in the registration form to the tier's `data-monthly-url`.
	 *
	 * It does nothing if `this.registerForm` is not defined.
	 */
	setFormDestToSelectedTier = () => {
		const tierCards = Array.from(document.querySelectorAll('.cusa-guardians-card'));
		const selectedTier = tierCards.find((tierCard) => tierCard.dataset.selected === 'true');

		const selectedMonthlyUrl = selectedTier.querySelector('button').dataset.monthlyUrl;

		if (!this.registerForm) return;

		this.registerForm.querySelector('[name="return_to"]').value = selectedMonthlyUrl;
	};

	/**
	 * Resets the Update Subscription button back to normal state
	 */
	resetButtonState = () => {
		if (!this.externalSubmitBtn) return;

		// Reset button text only for membership pages; do not touch on register
		if (this.templateName !== 'register') {
			const buttonText = this.externalSubmitBtn.querySelector('span') || this.externalSubmitBtn;
			if (buttonText) buttonText.textContent = 'Update subscription';
		}

		// Do not force-enable or change disabled class here; allow updateButtonStates()
		// to control enabled/disabled based on selection parity with current tier.
		// Only clear transient inline styles to avoid stuck visual artifacts.
		this.externalSubmitBtn.style.opacity = '';
		this.externalSubmitBtn.style.cursor = '';
	};

	/**
	 * Disables the update button after successful submission
	 */
	disableUpdateButton = () => {
		if (this.externalSubmitBtn) {
			this.externalSubmitBtn.disabled = true;
			this.externalSubmitBtn.classList.add('disabled');
		}
	};
}

customElements.define('cusa-guardians-tiers', CusaGuardiansTiers);
